import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { IConfigRetailUser, RootState } from 'constants/interfaces'
import avatarPlaceholder from 'assets/avatar-placeholder.svg'

interface ContextInfo {
  name: string
  avatar: string
  groupName?: string
}

/**
 * Hook to get context (user or group) name and avatar
 * If id is a userId - returns user's name and avatar
 * If id is a groupId - returns first user's name and avatar from that group
 */
const useContextInfo = (contextId: string | undefined): ContextInfo => {
  const { retailUsersObject, mappedRetailUsersByGroups, mappedGroupsByUsers, groups } = useSelector(
    (state: RootState) => state.config.config
  )

  const contextInfo = useMemo(() => {
    if (!contextId) {
      return { name: '', avatar: avatarPlaceholder }
    }

    // Check if it's a user ID (exists in retailUsersObject)
    const user = retailUsersObject?.[contextId]
    if (user) {
      const firstName = user.first_name || ''
      const lastName = user.last_name || ''
      const fullName = firstName + (lastName ? ' ' + lastName : '')
      const avatar = user.profile_img_url || user.avatar_profile_img_url || avatarPlaceholder
      const groupId = mappedGroupsByUsers?.[contextId]?.[0]

      return {
        name: fullName,
        avatar,
        groupName: groupId ? groups?.[groupId]?.name : undefined,
      }
    }

    // Not a user - check if it's a group ID
    const usersInGroup = mappedRetailUsersByGroups?.[contextId]
    if (usersInGroup && usersInGroup.length > 0) {
      // Get first user from the group
      const firstUserId = usersInGroup[0]
      const firstUser = retailUsersObject?.[firstUserId]

      if (firstUser) {
        const firstName = firstUser.first_name || ''
        const lastName = firstUser.last_name || ''
        const fullName = firstName + (lastName ? ' ' + lastName : '')
        const avatar = firstUser.profile_img_url || firstUser.avatar_profile_img_url || avatarPlaceholder

        return {
          name: fullName,
          avatar,
          groupName: groups?.[contextId]?.name,
        }
      }
    }

    // Fallback - ID not found in users or groups
    return {
      name: groups?.[contextId]?.name || contextId, // Return the ID itself as name
      avatar: avatarPlaceholder,
      groupName: groups?.[contextId]?.name,
    }
  }, [contextId, retailUsersObject, mappedRetailUsersByGroups, mappedGroupsByUsers, groups])

  return contextInfo
}

export default useContextInfo

/**
 * Non-hook version for use outside React components
 */
export const getContextInfo = (
  contextId: string | undefined,
  retailUsersObject: { [key: string]: IConfigRetailUser },
  mappedRetailUsersByGroups: { [key: string]: string[] }
): ContextInfo => {
  if (!contextId) {
    return { name: '', avatar: avatarPlaceholder }
  }

  // Check if it's a user ID
  const user = retailUsersObject?.[contextId]
  if (user) {
    const firstName = user.first_name || ''
    const lastName = user.last_name || ''
    const fullName = firstName + (lastName ? ' ' + lastName : '')
    const avatar = user.profile_img_url || user.avatar_profile_img_url || avatarPlaceholder

    return {
      name: fullName,
      avatar,
    }
  }

  // Check if it's a group ID
  const usersInGroup = mappedRetailUsersByGroups?.[contextId]
  if (usersInGroup && usersInGroup.length > 0) {
    const firstUserId = usersInGroup[0]
    const firstUser = retailUsersObject?.[firstUserId]

    if (firstUser) {
      const firstName = firstUser.first_name || ''
      const lastName = firstUser.last_name || ''
      const fullName = firstName + (lastName ? ' ' + lastName : '')
      const avatar = firstUser.profile_img_url || firstUser.avatar_profile_img_url || avatarPlaceholder

      return {
        name: fullName,
        avatar,
      }
    }
  }

  // Fallback
  return {
    name: contextId,
    avatar: avatarPlaceholder,
  }
}
